import { memo, useEffect, useState } from "react";
import { useStore } from "../store/globalState";
import { Trans, useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  AuraLayout,
  Button,
  GradientText,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { APP_ROUTES, flavorClassName, isFlavorB } from "../constants";
import KapitalRLogo from "../assets/KapitalLogoR.svg";
import "../styles/landingpage.style.css";
import "../styles/legal.style.css";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useClaimLink } from "../hooks/useClaimLink";
import { usePartnerLogoSrc } from "../hooks/usePartnerLogoSrc";
import { useSessionStore } from "../store/useSessionStore";
import { acceptTerms } from "../services/api";
import { getEvents, safeCapture } from "../analytics";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";

export const LandingPage = memo(() => {
  useClaimLink();
  const { t, i18n } = useTranslation();
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, title, bodyCopy, backgroundIcon } =
    colorConfig;
  const { redirectTo } = useRouteWithToken();
  const { session, updateSession } = useSessionStore();
  const linkId = session.data?.linkId;
  const { src: logoSrc, settled: logoSettled } = usePartnerLogoSrc(linkId);
  const [isESignChecked, setIsESignChecked] = useState(false);
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  useEffect(() => {
    if (!session.data?.termsAccepted) return;
    redirectTo(APP_ROUTES.TRANSACTIONS_PAGE, { replace: true });
  }, [session.data?.termsAccepted, redirectTo]);

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("welcome"));
  }, []);

  const handleContinue = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setSubmitError(false);
    safeCapture(getEvents().ACCEPT_BUTTON_CLICK);

    try {
      if (!linkId) throw new Error("TERMS_ACCEPT_FAILED");
      await acceptTerms(linkId, {
        documents: ["esign", "userAgreement"],
        language: i18n.language,
      });
      if (session.data) {
        updateSession({ ...session.data, termsAccepted: true });
      }
      safeCapture(getEvents().TERMS_ACCEPTED, { success: true });
      redirectTo(APP_ROUTES.TRANSACTIONS_PAGE);
    } catch {
      safeCapture(getEvents().TERMS_ACCEPTED, {
        success: false,
        error_code: "TERMS_ACCEPT_FAILED",
      });
      setSubmitError(true);
      setIsSubmitting(false);
    }
  };

  const content = isFlavorB ? (
    <section className="flow-hero">
      {/* Brand mark, so it keeps the glass disc instead of the elevated one. */}
      <div className="flow-hero-mark">
        <img
          src={KapitalRLogo}
          alt="app logo"
          className="landing-logo gradient-card glass"
        />
      </div>
      <section className="landing-container">
        <h1 className="foreground highlight text-centered">
          <Trans i18nKey="landing.startTransferring" />
        </h1>
        <p className="muted-foreground">{t("landing.followSteps2")}</p>
      </section>
    </section>
  ) : (
    <section className="flow-hero">
      <FlowHeroMark backgroundIcon={backgroundIcon}>
        {!logoSettled ? null : logoSrc ? (
          <img
            src={logoSrc}
            alt={session.data?.partner?.name ?? ""}
            className="welcome-logo"
          />
        ) : (
          <span
            className="welcome-logo-placeholder"
            aria-hidden="true"
            style={{ borderColor: bodyCopy, color: bodyCopy }}
          />
        )}
      </FlowHeroMark>
      <section className="landing-container">
        <PageTitle
          highlight={
            <>
              {t("landing.greeting")} <br /> {t("landing.toTransfer")}
            </>
          }
          highlightColor={title}
          secudnary={
            <>
              {t("landing.moneyFrom")} <br />
              {t("landing.usAccount")}
            </>
          }
          secudnaryColor={title}
        />
        <BodyCopy textColor={bodyCopy}>
          {t("landing.followSteps")}
          <br />
          {t("landing.madeSimple")}
        </BodyCopy>
      </section>
    </section>
  );
  const contentClassName =
    "landing-section landing-agreements-section flow-hero-section";
  const layoutClassName = `white-view-background landing-agreements-layout flow-landing-page ${flavorClassName}`;
  return (
    <AuraLayout colorConfig={colorConfig} className={layoutClassName}>
      <Layout.Content className={contentClassName}>
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        {content}
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          <section className="landing-policies">
            <span
              className={`policy-checkbox${
                isESignChecked ? "" : " policy-checkbox--required"
              }`}
            >
              <input
                id="e-sign"
                type="checkbox"
                checked={isESignChecked}
                onChange={(e) => setIsESignChecked(e.target.checked)}
              />
            </span>
            <BodyCopy className="footer-text terms-text footer-link muted-foreground">
              <label htmlFor="e-sign">
                {t("landing.footer.esign-content")}
              </label>
              <GradientText
                as="label"
                className="link"
                textColor={title}
                onClick={() => redirectTo(`${APP_ROUTES.LEGAL_PAGE}?doc=esign`)}
              >
                {t("landing.footer.esign-link")}
              </GradientText>
            </BodyCopy>
          </section>
          <section className="landing-policies">
            <span
              className={`policy-checkbox${
                isTermsChecked ? "" : " policy-checkbox--required"
              }`}
            >
              <input
                id="check-confirm"
                type="checkbox"
                checked={isTermsChecked}
                onChange={(e) => setIsTermsChecked(e.target.checked)}
              />
            </span>
            <BodyCopy className="footer-text terms-text footer-link muted-foreground">
              <label>{t("landing.footer.terms-content")}</label>

              <GradientText
                as="label"
                className="link"
                textColor={title}
                onClick={() => redirectTo(`${APP_ROUTES.LEGAL_PAGE}?exclude=esign`)}
              >
                {t("landing.footer.terms-link")}
              </GradientText>
              <label>{t("landing.footer.terms-content-2")}</label>
            </BodyCopy>
          </section>
          {submitError && (
            <BodyCopy className="footer-text" textColor={bodyCopy}>
              {t("error.tryAgainLater")}
            </BodyCopy>
          )}
          <Button
            size="large"
            disabled={!isTermsChecked || !isESignChecked || isSubmitting}
            background={backgroundBtn}
            color={textColorBtn}
            onClick={handleContinue}
          >
            {t("common.continue")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
